/**
 * Update Prompt Component - مكون تحديث التطبيق
 *
 * Shows a dialog when app updates are available
 * Handles both service worker updates and version polling updates
 */

import { RefreshCw } from 'lucide-react';
import { memo, useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { confirmUpdate } from '@/utils/pwa';
import { resetVersionCache } from '@/utils/version-check';

interface UpdateEvent extends Event {
  detail?: {
    source?: string;
    currentVersion?: string;
    newVersion?: string;
    buildTime?: number;
  };
}

export const UpdatePrompt = memo(() => {
  const [showDialog, setShowDialog] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<UpdateEvent['detail']>();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdateAvailable = useCallback((event: UpdateEvent) => {
    setUpdateInfo(event.detail);
    setShowDialog(true);
  }, []);

  useEffect(() => {
    // Type assertion for custom event
    window.addEventListener(
      'app-update-available',
      handleUpdateAvailable as EventListener,
    );

    return () => {
      window.removeEventListener(
        'app-update-available',
        handleUpdateAvailable as EventListener,
      );
    };
  }, [handleUpdateAvailable]);

  const handleUpdateNow = useCallback(async () => {
    setIsUpdating(true);

    try {
      // Trigger the update (will reload the page)
      await confirmUpdate();
    } catch (error) {
      console.error('[UpdatePrompt] Update failed:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }, []);

  const handleLater = useCallback(() => {
    setShowDialog(false);
    // Reset version cache to allow showing dialog again on next detection
    setTimeout(() => {
      resetVersionCache();
    }, 1000);
  }, []);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent
        size="sm"
        className="sm:max-w-[425px]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            🔄 تحديث متوفر
          </DialogTitle>
          <DialogDescription className="pt-2">
            إصدار جديد من التطبيق متوفر الآن! ✨
            {updateInfo?.newVersion && updateInfo.newVersion !== 'dev' && (
              <span className="mt-2 block text-xs text-muted-foreground">
                📦 الإصدار: {updateInfo.newVersion}
              </span>
            )}
            {updateInfo?.buildTime && (
              <span className="block text-xs text-muted-foreground">
                📅 تاريخ الإصدار:{' '}
                {new Date(updateInfo.buildTime).toLocaleString('ar-SA')}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground">
            🚀 هل تريد إعادة تشغيل التطبيق للحصول على أحدث الميزات والتحسينات؟
          </p>
          {updateInfo?.source && (
            <p className="mt-2 text-xs text-muted-foreground">
              📡 المصدر:{' '}
              {updateInfo.source === 'service-worker'
                ? 'عامل الخدمة'
                : updateInfo.source === 'version-check'
                  ? 'فحص الإصدار'
                  : updateInfo.source === 'websocket'
                    ? 'تحديث فوري'
                    : updateInfo.source}
            </p>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleLater} disabled={isUpdating}>
            ⏰ لاحقاً
          </Button>
          <Button
            onClick={handleUpdateNow}
            disabled={isUpdating}
            className="gap-2"
          >
            {isUpdating ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                🔄 جاري التحديث...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />⚡ تحديث الآن
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
